import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, PencilSimple, X, FileText, Archive } from "@phosphor-icons/react";

export default function Quotes() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => setItems((await api.get("/quotes", { params: { archived: showArchived ? "true" : "false" } })).data);
  useEffect(() => {
    load();
    api.get("/settings").then((r) => setTiers(r.data.discount_tiers || [])).catch(() => {});
  }, [showArchived]);

  // Live client-side preview matching backend _apply_discount logic
  const preview = useMemo(() => {
    if (!editing) return null;
    const subtotal = editing.line_items.reduce(
      (s, li) => s + (parseFloat(li.qty) || 0) * (parseFloat(li.unit_price) || 0),
      0
    );
    const dur = parseFloat(editing.duration_hours) || 0;
    const eligible = (tiers || []).filter((t) => dur >= (parseFloat(t.min_hours) || 0));
    const tier = eligible.length
      ? eligible.reduce((a, b) => (parseFloat(a.discount_pct) >= parseFloat(b.discount_pct) ? a : b))
      : null;
    const pct = tier ? parseFloat(tier.discount_pct) || 0 : 0;
    const amount = Math.round(subtotal * pct) / 100;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      discount_pct: pct,
      discount_label: tier?.label || null,
      discount_amount: Math.round(amount * 100) / 100,
      total: Math.round((subtotal - amount) * 100) / 100,
    };
  }, [editing, tiers]);

  const approve = async (id) => {
    await api.post(`/quotes/${id}/approve`);
    toast.success("Quote sent");
    load();
  };
  const save = async () => {
    const payload = {
      title: editing.title,
      notes: editing.notes,
      duration_hours: parseFloat(editing.duration_hours) || 0,
      line_items: editing.line_items.map((li) => ({
        description: li.description,
        qty: parseFloat(li.qty) || 1,
        unit_price: parseFloat(li.unit_price) || 0,
      })),
    };
    const r = await api.put(`/quotes/${editing.quote_id}`, payload);
    if (r.data.discount_pct > 0) {
      toast.success(`Saved — ${r.data.discount_label} discount applied (${r.data.discount_pct}%)`);
    } else {
      toast.success("Quote updated");
    }
    setEditing(null);
    load();
  };

  const archive = async (id, currentlyArchived) => {
    await api.post(`/quotes/${id}/${currentlyArchived ? "unarchive" : "archive"}`);
    toast(currentlyArchived ? "Restored" : "Archived");
    load();
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-neutral-500 dark:text-neutral-500 mb-2">Quotes</div>
          <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>{showArchived ? "Archived quotes" : "Draft & sent quotes"}</h1>
        </div>
        <button
          data-testid="quotes-toggle-archived"
          onClick={() => setShowArchived((v) => !v)}
          className="text-xs px-3 py-2 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center gap-1"
        >
          <Archive size={12} /> {showArchived ? "Show active" : "Show archived"}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg p-10 text-center text-sm text-neutral-500 dark:text-neutral-500">
          No quotes yet. Draft a quote from any conversation in the Inbox.
        </div>
      ) : (
        <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-900 divide-y divide-neutral-100 dark:divide-neutral-800">
          {items.map((q) => (
            <div key={q.quote_id} data-testid={`quote-row-${q.quote_id}`} className="p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <FileText size={18} className="text-neutral-500 dark:text-neutral-500 shrink-0" weight="duotone" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{q.title}</div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-500">
                    {q.customer_name} · ${q.total?.toFixed?.(2) ?? "0.00"}
                    {q.discount_pct > 0 && (
                      <span className="ml-2 text-[#0F52BA]">
                        · {q.discount_label} −{q.discount_pct}% (saved ${q.discount_amount?.toFixed(2)})
                      </span>
                    )}
                    {q.duration_hours > 0 && (
                      <span className="ml-2 text-neutral-400 dark:text-neutral-500">· {q.duration_hours}h</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded border ${q.status === "draft" ? "border-[#FF3B30] text-[#FF3B30]" : "border-emerald-600 text-emerald-700"}`}>{q.status}</span>
                {q.status === "draft" && !showArchived && (
                  <>
                    <button data-testid={`quote-edit-${q.quote_id}`} onClick={() => setEditing(q)} className="text-xs px-3 py-1.5 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center gap-1"><PencilSimple size={14} /> Edit</button>
                    <button data-testid={`quote-approve-${q.quote_id}`} onClick={() => approve(q.quote_id)} className="text-xs px-3 py-1.5 rounded-md bg-[#0F52BA] text-white hover:bg-[#0C4094] flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Approve & send</button>
                  </>
                )}
                <button data-testid={`quote-archive-${q.quote_id}`} onClick={() => archive(q.quote_id, showArchived)} title={showArchived ? "Restore" : "Archive"} className="text-xs p-1.5 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800">
                  <Archive size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-neutral-900 rounded-lg p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500">Edit quote</div>
                <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="text-lg font-medium mt-1 w-full border-b border-neutral-200 dark:border-neutral-800 focus:outline-none focus:border-neutral-950" />
              </div>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded"><X size={18} /></button>
            </div>
            <div className="space-y-2 mb-4">
              {editing.line_items.map((li, idx) => (
                <div key={li._key || idx} className="grid grid-cols-12 gap-2">
                  <input value={li.description} onChange={(e) => { const arr = [...editing.line_items]; arr[idx] = { ...arr[idx], description: e.target.value }; setEditing({ ...editing, line_items: arr }); }} className="col-span-7 px-2 py-1.5 border border-neutral-300 dark:border-neutral-700 rounded text-sm bg-transparent" placeholder="Description" />
                  <input value={li.qty} onChange={(e) => { const arr = [...editing.line_items]; arr[idx] = { ...arr[idx], qty: e.target.value }; setEditing({ ...editing, line_items: arr }); }} className="col-span-2 px-2 py-1.5 border border-neutral-300 dark:border-neutral-700 rounded text-sm bg-transparent" placeholder="Qty" />
                  <input value={li.unit_price} onChange={(e) => { const arr = [...editing.line_items]; arr[idx] = { ...arr[idx], unit_price: e.target.value }; setEditing({ ...editing, line_items: arr }); }} className="col-span-3 px-2 py-1.5 border border-neutral-300 dark:border-neutral-700 rounded text-sm bg-transparent" placeholder="Price" />
                </div>
              ))}
            </div>
            <div className="mb-4 grid grid-cols-12 gap-2 items-center">
              <div className="col-span-5 text-xs uppercase tracking-[0.15em] text-neutral-500 dark:text-neutral-500">Duration (hours)</div>
              <input
                data-testid="quote-duration-input"
                type="number"
                min="0"
                step="0.5"
                value={editing.duration_hours ?? 0}
                onChange={(e) => setEditing({ ...editing, duration_hours: e.target.value })}
                className="col-span-3 px-2 py-1.5 border border-neutral-300 dark:border-neutral-700 rounded text-sm bg-transparent"
                placeholder="0"
              />
              <div className="col-span-4 text-xs text-neutral-500 dark:text-neutral-500 text-right">
                {preview?.discount_pct > 0
                  ? <>Applies <span className="text-[#0F52BA]">−{preview.discount_pct}%</span> · save ${preview.discount_amount.toFixed(2)}</>
                  : "No discount tier"}
              </div>
            </div>
            <div className="mb-4 p-3 rounded-md bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-800 text-sm space-y-1">
              <div className="flex justify-between text-neutral-500 dark:text-neutral-400"><span>Subtotal</span><span>${(preview?.subtotal ?? 0).toFixed(2)}</span></div>
              {preview?.discount_pct > 0 && (
                <div className="flex justify-between text-[#0F52BA]">
                  <span>{preview.discount_label} discount</span>
                  <span>−${(preview.discount_amount ?? 0).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-medium border-t border-neutral-200 dark:border-neutral-800 pt-1 mt-1">
                <span>Total</span><span>${(preview?.total ?? 0).toFixed(2)}</span>
              </div>
            </div>
            <textarea value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} rows={3} className="w-full px-2 py-1.5 border border-neutral-300 dark:border-neutral-700 rounded text-sm mb-4" placeholder="Notes" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="text-sm px-4 py-2 rounded-md border border-neutral-300 dark:border-neutral-700">Cancel</button>
              <button data-testid="quote-save-edit" onClick={save} className="text-sm px-4 py-2 rounded-md bg-neutral-950 text-white dark:bg-neutral-100 dark:text-neutral-950 hover:bg-neutral-800">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
